<template>
  <div class="font-select">
    <v-select
      v-if="!showPasteInput"
      :value="value"
      @change="onFontSelect"
      :items="items"
      item-text="family"
      item-value="family"
      hide-details
      solo
    >
      <template v-slot:item="{ item }">
        <v-divider v-if="item.divider"></v-divider>
        <span v-else :style="itemStyle(item)">{{ item.family }}</span>
      </template>
      <template v-slot:selection="{ item }">
        <span :style="itemStyle(item)">{{ item.family }}</span>
      </template>
    </v-select>

    <div v-if="showPasteInput" class="paste-inline">
      <v-text-field
        v-model="pasteInput"
        :placeholder="$t('design_font_paste_placeholder')"
        solo
        hide-details
        autofocus
        @keydown.enter="applyPaste"
        @keydown.esc="cancelPaste"
      >
        <template v-slot:append>
          <v-btn icon @click="applyPaste" :title="$t('save')" :loading="loading" >
            <v-icon color="primary">mdi-check</v-icon>
          </v-btn>
          <v-btn icon @click="cancelPaste" :title="$t('cancel')" :disabled="loading" >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <div v-if="pasteError" class="paste-error mt-1">
        {{ pasteError }}
      </div>
    </div>
  </div>
</template>

<script>
import { ALL_FONTS, PREVIEW_STYLESHEET_URL, parseGoogleFontsInput, loadGoogleFont } from '@/utils/fontRegistry'

export default {
  name: 'FontSelect',
  props: {
    value: String,
    customFont: Object
  },
  data () {
    return {
      showPasteInput: false,
      pasteInput: '',
      pasteError: '',
      loading: false
    }
  },
  computed: {
    items () {
      const defaultItem = {
        family: this.$t('design_font_default'),
        cssFamily: null,
        source: 'default',
        cssUrl: null
      }
      const customFontAction = {
        family: this.$t('design_font_paste_toggle'),
        cssFamily: null,
        source: '__custom_font__',
        cssUrl: null
      }
      const items = [defaultItem, customFontAction]
      if (this.customFont) {
        items.push({ ...this.customFont })
      }
      items.push({ divider: true }, ...ALL_FONTS)
      return items
    }
  },
  methods: {
    onFontSelect (familyName) {
      if (familyName === this.$t('design_font_paste_toggle')) {
        this.showPasteInput = true
        return
      }
      if (familyName === this.$t('design_font_default')) {
        this.$emit('select', null)
        return
      }
      if (this.customFont && this.customFont.family === familyName) {
        this.$emit('select', { ...this.customFont })
        return
      }
      const font = ALL_FONTS.find(f => f.family === familyName)
      if (font) {
        this.$emit('select', { ...font })
      }
    },
    async applyPaste () {
      this.pasteError = ''
      const parsed = parseGoogleFontsInput(this.pasteInput)
      if (!parsed) {
        this.pasteError = this.$t('design_font_paste_invalid')
        return
      }
      this.loading = true
      try {
        await loadGoogleFont(parsed.cssUrl)
        this.$emit('custom-font', parsed)
        this.showPasteInput = false
        this.pasteInput = ''
      } catch (e) {
        this.pasteError = this.$t('design_font_load_failed')
      } finally {
        this.loading = false
      }
    },
    cancelPaste () {
      this.showPasteInput = false
      this.pasteInput = ''
      this.pasteError = ''
    },
    itemStyle (item) {
      if (item.source === 'default') return { fontFamily: 'Assistant' }
      if (item.source === '__custom_font__') return {}
      if (!item.cssFamily) return {}
      return { fontFamily: item.cssFamily }
    }
  },
  created () {
    loadGoogleFont(PREVIEW_STYLESHEET_URL).catch(() => {})
  }
}
</script>

<style lang="sass" scoped>
.font-select
  flex: 1

.paste-error
  color: #f43838
  font-size: 12px
</style>

<style lang="sass">
@font-face
  font-family: 'Assistant'
  src: url('../assets/fonts/Assistant-Regular.ttf') format('truetype')
</style>
